<script setup>
import TvClubLogo from '~/components/tv/TvClubLogo.vue';
import {tvWhen} from '~/utils/tvDisplay';

// Opens every loop: the club itself, then what's next
const props = defineProps({
  panel: {type: Object, required: true},
  clubName: {type: String, required: true},
  logo: {type: String, default: null},
  // The competition panels on this screen: [{title, icon}]
  running: {type: Array, default: () => []},
});

const splash = computed(() => props.panel.splash ?? {});

const summary = computed(() => {
  const parts = [];
  const s = splash.value;
  // Counted from the list beneath, so the number matches what's shown
  const running = props.running.length || s.activeCompetitions;
  if (running) parts.push(`${running} ${running === 1 ? 'competition' : 'competitions'} running`);
  if (s.resultsThisWeek) parts.push(`${s.resultsThisWeek} ${s.resultsThisWeek === 1 ? 'result' : 'results'} this week`);
  if (s.matchesComingUp) parts.push(`${s.matchesComingUp} ${s.matchesComingUp === 1 ? 'match' : 'matches'} coming up`);
  return parts;
});
</script>

<template>
  <div class="tv-splash">
    <tv-club-logo :filename="logo" :club-name="clubName" :size="260"/>
    <div class="tv-splash-text">
      <h2 class="tv-splash-name">{{ clubName }}</h2>
      <ul v-if="summary.length" class="tv-splash-facts">
        <li v-for="fact in summary" :key="fact">{{ fact }}</li>
      </ul>
      <ul v-if="running.length" class="tv-splash-running">
        <li v-for="item in running.slice(0, 8)" :key="item.title">
          <v-icon :icon="item.icon" size="30"/>
          <span>{{ item.title }}</span>
        </li>
      </ul>
      <div v-if="splash.nextMatch" class="tv-splash-next">
        <span class="tv-splash-next-when">Next up, {{ tvWhen(splash.nextMatch.scheduledAt) }}</span>
        <span class="tv-splash-next-teams">{{ splash.nextMatch.homeTeamName }} v {{ splash.nextMatch.awayTeamName }}</span>
        <span v-if="splash.nextMatch.playingAreaName" class="tv-splash-next-court">{{ splash.nextMatch.playingAreaName }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tv-splash {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 88px;
  align-items: center;
  padding: 36px 24px 0;
}


.tv-splash-name {
  margin: 0;
  font-family: var(--tv-display);
  font-weight: 800;
  font-size: 120px;
  line-height: 0.95;
  color: var(--tv-ink);
}

.tv-splash-facts {
  list-style: none;
  margin: 28px 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 40px;
  font-size: 34px;
  color: var(--tv-muted);
}

.tv-splash-running {
  list-style: none;
  margin: 40px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 520px));
  gap: 14px 32px;

  li {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 20px;
    border-radius: 10px;
    background: var(--tv-panel);
    border: 2px solid var(--tv-line);
    font-size: 30px;
    color: var(--tv-ink);
    min-width: 0;
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .v-icon {
    color: var(--tv-accent-text);
  }
}

.tv-splash-next {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 24px 32px;
  border-radius: 12px;
  background: var(--tv-panel);
  border: 2px solid var(--tv-line);
  box-shadow: inset 10px 0 0 var(--tv-accent);
  max-width: 1100px;
}

.tv-splash-next-when {
  font-size: 28px;
  color: var(--tv-accent-text);
  font-weight: 600;
}

.tv-splash-next-teams {
  font-family: var(--tv-display);
  font-weight: 700;
  font-size: 58px;
  color: var(--tv-ink);
}

.tv-splash-next-court {
  font-size: 26px;
  color: var(--tv-muted);
}

/* Portrait: logo above, everything centred in one column */
:global(.tv-narrow .tv-splash) {
  grid-template-columns: 1fr;
  justify-items: center;
  text-align: center;
  gap: 56px;
  padding-top: 80px;
}

:global(.tv-narrow .tv-splash-facts) {
  justify-content: center;
}

:global(.tv-narrow .tv-splash-running) {
  grid-template-columns: minmax(0, 760px);
  justify-content: center;
  text-align: left;
}

:global(.tv-narrow .tv-splash-next) {
  margin-left: auto;
  margin-right: auto;
}
</style>
